import {MediaType} from '@colibrio/colibrio-reader-framework/colibrio-core-io-base';
import {IEpubPublication} from '@colibrio/colibrio-reader-framework/colibrio-core-publication-epub';
import {VanillaReaderPublication} from '../VanillaReader/VanillaReaderPublication';
import {EpubDataNavigationDocument, Region} from './EpubDataNavigationDocument';
import {EPUBStructuralSematicsVocabulary} from './EPUBStructuralSemanticsVocabulary';
import {
    IVanillaMediaObjectData,
    IVanillaReaderGuidedReadingStepData,
    IVanillaReaderGuidedReadingTimelineData,
} from './VanillaReaderGuidedReadingModel';
import {IPublicationMetadataItem} from "@colibrio/colibrio-reader-framework/colibrio-core-publication-base";
import {MediaFragmentParser} from "../utils/MediaFragmentParser";

/**
 * # VanillaRegionBasedNavigation
 *
 * ## RESPONSIBILITIES
 *
 *
 * ## RELATED TYPES
 *
 * - VanillaReaderPublication
 * - EpubDataNavigationDocument
 *
 * */
export class VanillaRegionBasedNavigation {
    private _vanillaPublication: VanillaReaderPublication;
    private _navigationDocument: EpubDataNavigationDocument | undefined;

    private constructor(vanillaPublication: VanillaReaderPublication) {
        this._vanillaPublication = vanillaPublication;
    }

    static publicationHasRegionBasedNavigation(vanillaPublication: VanillaReaderPublication): boolean {
        let srcPublication = vanillaPublication.getColibrioReaderPublication().getSourcePublication() as IEpubPublication;
        return srcPublication.getOpfDocument().querySelector('[properties="data-nav"]') !== null;
    }

    static async create(vanillaPublication: VanillaReaderPublication): Promise<VanillaRegionBasedNavigation> {
        let instance: VanillaRegionBasedNavigation = new VanillaRegionBasedNavigation(vanillaPublication);

        let srcPublication = instance._vanillaPublication.getColibrioReaderPublication().getSourcePublication() as IEpubPublication;
        let dataNavManifestElement = srcPublication.getOpfDocument().querySelector('[properties="data-nav"]');

        if (!dataNavManifestElement) {
            throw new Error('VanillaRegionBasedNavigation.create(), No Data Navigation Document found in EPUB OPF manifest.');
        }

        let opfBasePath = srcPublication.getOpfDocumentUrl().pathname.slice(0, srcPublication.getOpfDocumentUrl().pathname.lastIndexOf('/'));
        let dataNavPath = `${opfBasePath}/${dataNavManifestElement.getAttribute('href')}`;
        let dataNavUrl: string | undefined = await instance._vanillaPublication.fetchResourceBlobUrl(dataNavPath);

        if (!dataNavUrl) {
            throw new Error(`VanillaRegionBasedNavigation.create(), Unable to create Blob URL for file "${dataNavPath}".`);
        }

        let dataNavResponse = await fetch(dataNavUrl);

        if (!dataNavResponse) {
            throw new Error(`VanillaRegionBasedNavigation.create(), Unable to fetch XHTML document for file "${dataNavPath}".`);
        }

        let domParser = new DOMParser();
        let dataNavDom = domParser.parseFromString(await dataNavResponse.text(), 'text/html');

        if (!dataNavDom) {
            throw new Error(`VanillaRegionBasedNavigation.create(), Unable to parse XHTML source for file "${dataNavPath}".`);
        }

        instance._navigationDocument = new EpubDataNavigationDocument(dataNavDom);

        return instance;

    }

    toGuidedReadingTimelineData(): IVanillaReaderGuidedReadingTimelineData {
        let stepData: IVanillaReaderGuidedReadingStepData[] = [];

        if (!this._navigationDocument || !this._vanillaPublication) {
            throw new Error(`VanillaRegionBasedNavigation.toGuidedReadingTimelineData(), VanillaRegionBasedNavigation instance has not been created.".`);
        }

        const mapRegionsToSteps = (regions: Region[]): IVanillaReaderGuidedReadingStepData[] => {

            let steps: IVanillaReaderGuidedReadingStepData[] = [];
            let childSteps: IVanillaReaderGuidedReadingStepData[] = [];

            for (const region of regions) {

                childSteps = [];
                let mediaObjects: IVanillaMediaObjectData[] = [];
                let alternateAudioMetaDataItem: IPublicationMetadataItem | undefined;

                let matchingSpineItem = this._vanillaPublication?.getColibrioReaderPublication().getSpine().find((spineItem) => {
                    return region.uri !== undefined ?
                        spineItem.getSourceContentDocument().getContentUrl().pathname.includes(region.uri.slice(0, region.uri.indexOf('#'))) :
                        false;
                });

                if (!matchingSpineItem) {
                    continue;
                }

                if(region.regions.length > 0) {
                    childSteps = mapRegionsToSteps(region.regions);
                }

                mediaObjects = [
                    {
                        mediaType: MediaType.APPLICATION_EPUB_ZIP,
                        url: matchingSpineItem.getContentLocation().getLocator().toString(),
                        selectors: region.selectors || [],
                    },
                ]

                alternateAudioMetaDataItem = region.metadata.find(item => item.property.name === 'alternate');

                if(alternateAudioMetaDataItem) {
                    mediaObjects.push({
                        mediaType: MediaType.AUDIO_MPEG,
                        url: alternateAudioMetaDataItem.content.value,
                        selectors: MediaFragmentParser.parseFragmentSelectorsFromUrl(alternateAudioMetaDataItem.content.value)
                    })
                }

                steps.push({
                    locator: matchingSpineItem.getContentLocation().getLocator().toString(),
                    stepType: region.typeName || EPUBStructuralSematicsVocabulary.COMICS_PANEL,
                    mediaObjects,
                    steps: childSteps || []
                });
            }

            return steps;
        }

        this._navigationDocument.navs.forEach((nav) => {
            stepData = mapRegionsToSteps(nav.regions);
        });

        return {
            publicationId: this._vanillaPublication.id,
            steps: stepData,
            tracks: []
        };
    }

    getDataNavDocument(): EpubDataNavigationDocument | undefined {
        return this._navigationDocument;
    }
}
